import { TextValidatorType } from "anketa-core/base";
import { Group, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { TextValidator } from "anketa-core/validation";

export const setupIcinga2Group = (icinga2Scanners: Section, idx = 0) => {
  const icinga2Group = icinga2Scanner(idx);
  icinga2Scanners.addChild(icinga2Group.icinga2Scanner);

  function onConfigChange() {
    const name = icinga2Group.icinga2Name.fact.raw;

    if (name && name.toString().trim() !== "") {
      icinga2Group.icinga2Name.removeChangeListener(onConfigChange);
      setupIcinga2Group(icinga2Scanners, idx + 1);
    }
  }

  icinga2Group.icinga2Name.addChangeListener(onConfigChange);
};

export const icinga2Scanner = (
  idx: number
): { icinga2Scanner: Group; icinga2Name: OpenQuestion } => {
  const idPrefix = `icinga_scanner_${idx}`;

  const icinga2Scanner = new Group(idPrefix, [
    new Translation("en", "Unconfigured Icinga2 Scanner"),
    new Translation("de", "Unkonfigurierter Icinga2 Scanner"),
  ]);

  const name = new OpenQuestion(`${idPrefix}_name`, [
    new Translation("en", "Display name"),
    new Translation("de", "Anzeigename"),
  ]);
  name.validator = new TextValidator();

  name.required = true;
  name.addChangeListener(() => {
    const plainName = name.fact.raw.toString();
    if (plainName.trim() !== "") {
      icinga2Scanner.title = [
        new Translation("en", plainName),
        new Translation("de", plainName),
      ];
    } else {
      icinga2Scanner.title = [
        new Translation("en", "Unconfigured Icinga2 Scanner"),
        new Translation("de", "Unkonfigurierter Icinga2 Scanner"),
      ];
    }
  });
  icinga2Scanner.addChild(name);

  const icingaUrl = new OpenQuestion(`${idPrefix}_url`, [
    new Translation("en", "Icinga 2 API URL"),
    new Translation("de", "Icinga 2 API-URL"),
  ]);
  icingaUrl.validator = new TextValidator(TextValidatorType.Uri);

  icingaUrl.required = true;
  icingaUrl.help = [
    new Translation(
      "en",
      "The full URL to your Icinga 2 API endpoint (e.g., https://icinga2.example.com:5665/v1)."
    ),
    new Translation(
      "de",
      "Die vollständige URL zu Ihrem Icinga 2 API-Endpunkt (z.B. https://icinga2.beispiel.de:5665/v1)."
    ),
  ];
  icinga2Scanner.addChild(icingaUrl);

  const icingaWebUiUrl = new OpenQuestion(`${idPrefix}_webuiurl`, [
    new Translation("en", "Icinga 2 Web UI URL"),
    new Translation("de", "Icinga 2 Web-UI-URL"),
  ]);
  icingaWebUiUrl.validator = new TextValidator(TextValidatorType.Uri);

  icingaWebUiUrl.help = [
    new Translation(
      "en",
      "The URL to your Icinga 2 Web UI for direct access from the integration (optional)."
    ),
    new Translation(
      "de",
      "Die URL zu Ihrer Icinga 2 Weboberfläche für den Direktzugriff aus der Integration (optional)."
    ),
  ];
  icinga2Scanner.addChild(icingaWebUiUrl);

  const icingaUsername = new OpenQuestion(`${idPrefix}_username`, [
    new Translation("en", "API Username"),
    new Translation("de", "API-Benutzername"),
  ]);
  icingaUsername.validator = new TextValidator();

  icingaUsername.required = true;
  icingaUsername.help = [
    new Translation(
      "en",
      "The username of the API user with read-only permissions."
    ),
    new Translation(
      "de",
      "Der Benutzername des API-Benutzers mit Nur-Lesen-Rechten."
    ),
  ];
  icinga2Scanner.addChild(icingaUsername);

  const icingaPassword = new OpenQuestion(`${idPrefix}_password`, [
    new Translation("en", "API Password"),
    new Translation("de", "API-Passwort"),
  ]);
  icingaPassword.required = true;
  icingaPassword.readonly = true;
  icingaPassword.help = [
    new Translation("en", "The password for the API user."),
    new Translation("de", "Das Passwort für den API-Benutzer."),
  ];
  icinga2Scanner.addChild(icingaPassword);

  const icingaInsecure = new OptionsQuestion(
    `${idPrefix}_insecure`,
    [
      new Translation("en", "Disable Certificate Verification"),
      new Translation("de", "Zertifikatsüberprüfung deaktivieren"),
    ],
    "default.options.yes_no"
  );
  icingaInsecure.help = [
    new Translation(
      "en",
      "Should SSL/TLS certificate verification be disabled? Only use this for testing with self-signed certificates."
    ),
    new Translation(
      "de",
      "Soll die Überprüfung von SSL/TLS-Zertifikaten deaktiviert werden? Nur für Tests mit selbstsignierten Zertifikaten verwenden."
    ),
  ];
  if (icingaInsecure.fact.raw === null) {
    icingaInsecure.setFactValue(1);
  }
  icinga2Scanner.addChild(icingaInsecure);

  return { icinga2Scanner, icinga2Name: name };
};
